import React, { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import Dropzone from "react-dropzone";
import { createWorker } from "tesseract.js";
import { Button, ButtonGroup, Form, Modal } from "react-bootstrap";
import {
  SCHOOL_DATA_CONSTANTS,
  DEVICE_INFO_OBJ,
  DEVICE_TYPE,
} from "../constants/schoolData";
import { Spinner } from "react-bootstrap";
import "font-awesome/css/font-awesome.min.css";
import { EVENT_NAMES_CONSTANT } from "../services/events";

function TVEnrolmentComponent({
  deviceId,
  setDeviceInfo,
  onClickEnroll,
  isBtnDisabled,
  message,
  enrollmentStatus,
  validationError,
  handleTVBtnClickEvent,
  handleTVEnrolEvent,
}) {
  const webcamRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openWebCam, setOpenWebcam] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState(undefined);
  const [errorMessage, setErrorMsg] = useState(undefined);

  useEffect(() => {
    let error = "";
    if (deviceId.length) {
      if (!/^[A-Za-z0-9]{16}$/.test(deviceId)) {
        error = SCHOOL_DATA_CONSTANTS.tvEnrollmentErrors.invalid;
      }

      setDeviceInfo((prevState) => ({
        ...prevState,
        validationMsg: error,
      }));

      handleTVEnrolEvent(EVENT_NAMES_CONSTANT.enrolment_submitted);
    }
  }, [deviceId]);

  const captureImage = async (videoSrc, triggerFrom) => {
    const worker = await createWorker({
      logger: (m) => {
        if (!isLoading && m.status === "recognizing text") setIsLoading(true);
        if (Math.round(m.progress * 100) === 100) setIsLoading(false);
        // console.log(m);
      },
    });
    const image = new Image();
    image.src = videoSrc;

    await worker.loadLanguage("eng");
    await worker.initialize("eng");
    await worker.setParameters({
      tessedit_char_whitelist: "0123456789abcdefghijklmnopqrstuvwxyz",
    });
    const { data } = await worker.recognize(image);

    // if (type === "upload") {
    //   setDeviceInfo((prevState) => ({
    //     ...prevState,
    //     id: data.text?.split(/\s+/g)[0].trim(),
    //   }));
    // } else {
    //   // if image is taken directly from camera then remove special characters
    //   setDeviceInfo((prevState) => ({
    //     ...prevState,
    //     id: data.text?.replace(/[^a-zA-Z0-9 ]/g, ""),
    //   }));
    // }

    setDeviceInfo((prevState) => ({
      ...prevState,
      id: removeVersionCode(data.text),
      triggerFrom,
    }));
    await worker.terminate();
  };

  const handleDrop = (acceptedFiles) => {
    let imageFiles = acceptedFiles;
    if (acceptedFiles.length > 0) {
      imageFiles = acceptedFiles.filter((file) =>
        file.type.startsWith("image/")
      );
    }
    if (imageFiles.length === 0) {
      setErrorMsg("invalid file selected");
      return;
    }

    setErrorMsg(undefined);
    setOpenWebcam(false);
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      captureImage(reader.result, DEVICE_TYPE.gallery);
    };
    reader.readAsDataURL(imageFiles[0]);
  };

  const handleClick = () => {
    handleCloseModal();
    setIsModalOpen(true);
    handleTVBtnClickEvent();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setDeviceInfo(DEVICE_INFO_OBJ);
    setOpenWebcam(false);
    setIsLoading(false);
    setImageSrc(undefined);
    setErrorMsg(undefined);
  };

  const captureImageFromWebcam = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setImageSrc(imageSrc);
    captureImage(imageSrc, DEVICE_TYPE.camera);
    setOpenWebcam(false);
  };

  const handleOpenWebcam = () => {
    setImageSrc(undefined);
    setOpenWebcam(true);
    setErrorMsg(undefined);
  };

  function removeVersionCode(string) {
    const parts = string.split(/\s+/g); // Split the string into an array of substrings at each space character
    const result = parts[0]; // Get the first element of the array (text before the first space)
    return result?.replace(/[^a-zA-Z0-9 ]/g, "").trim(); // replace special charcters if exist
  }

  const onChangeTextField = (e) => {
    const id = removeVersionCode(e.target.value);
    setDeviceInfo((prevState) => ({
      ...prevState,
      triggerFrom:
        prevState.triggerFrom !== DEVICE_TYPE.manual
          ? prevState.triggerFrom
          : DEVICE_TYPE.manual,
      id,
      isFieldChanged: true,
    }));
  };

  return (
    <div className="tv-enrollment-container">
      <ButtonGroup size="sm">
        <Button variant="light" onClick={handleClick}>
          <i className="fa fa-camera" aria-hidden="true"></i>
          <span className="ml-2">TV Enrollment</span>
        </Button>
      </ButtonGroup>
      <Modal
        show={isModalOpen}
        onHide={handleCloseModal}
        className="ocr-modal-body tv-enrollment-model"
      >
        <Modal.Header closeButton>
          <Modal.Title>TV Enrolment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message && (
            <div
              className={
                enrollmentStatus === "OK" ? "success-message" : "error-message"
              }
            >
              {message}
            </div>
          )}
          {errorMessage && (
            <div className={"error-message"}>{errorMessage}</div>
          )}
          <div className="TextScannerComponent">
            {!!imageSrc ? (
              <img src={imageSrc} className="modal-image" alt="Modal Image" />
            ) : (
              openWebCam && (
                <Webcam
                  videoConstraints={{
                    facingMode: "environment",
                  }}
                  screenshotQuality="1"
                  onUserMediaError={(error) => console.error(error)}
                  ref={webcamRef}
                />
              )
            )}
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                marginTop: "10px",
                marginBottom: "10px",
                justifyContent: "space-evenly",
              }}
            >
              <span
                style={{
                  fontSize: "20px",
                  marginRight: "10px",
                  whiteSpace: "nowrap",
                }}
              >
                Device Id:
              </span>
              <div className="dflex dflex-column device-id-input-box">
                <>
                  {!isLoading && (
                    <>
                      <Form.Control
                        onChange={(e) => onChangeTextField(e)}
                        className="device-info"
                        style={{ fontSize: "20px" }}
                        id="inlineFormInput"
                        placeholder="Please enter Device Id"
                        type={deviceId}
                        value={deviceId}
                        isInvalid={!!validationError}
                      />
                      <div className="mt-2 text-danger">{validationError}</div>
                    </>
                  )}
                  {!!isLoading && (
                    <div className="dflex dflex-justify-center dflex-align-center">
                      <Spinner
                        animation="border"
                        content={isLoading}
                        variant="success"
                      />
                    </div>
                  )}
                </>
              </div>
            </div>
            <div
              style={{
                textAlign: "right",
                display: "flex",
                paddingTop: "10px",
              }}
            >
              <Dropzone onDrop={handleDrop} multiple={false} accept="image/*">
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()} style={{ flex: "1" }}>
                    <input {...getInputProps({ accept: "image/*" })} />
                    <div className="btn-regular">
                      <Button>Select from Gallery</Button>
                    </div>
                  </div>
                )}
              </Dropzone>
              {!openWebCam ? (
                <div className="btn-regular">
                  <Button onClick={handleOpenWebcam}>Open Camera</Button>
                </div>
              ) : (
                <div className="btn-regular">
                  <Button onClick={captureImageFromWebcam}>
                    Capture Image
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="dflex dflex-space-between">
          {message ? (
            <Button
              disabled={isBtnDisabled}
              className="popover-logout-btn"
              onClick={handleCloseModal}
            >
              Close
            </Button>
          ) : (
            <div />
          )}
          <Button
            disabled={isBtnDisabled}
            className="popover-logout-btn"
            onClick={onClickEnroll}
          >
            Enroll TV
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TVEnrolmentComponent;
